import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePopoverPosition } from "../hooks/usePopoverPosition";
import {
  addDays,
  addMonths,
  firstWeekdayOfMonth,
  formatLocalParts,
  formatLocalPartsShort,
  isSameDay,
  isoToLocalParts,
  localPartsToIso,
  nowLocalParts,
  type LocalDateTimeParts,
} from "../utils/dateTimeParts";
import "./DateTimePicker.css";

/** Minute granularity offered by default. 5 matches how a coach books a call slot. */
const DEFAULT_MINUTE_STEP = 5;

/** Default time applied when a day is picked before any time has been chosen. */
const DEFAULT_HOUR_24 = 9;

const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

/** Keep in step with the panel width in the stylesheet; used for viewport clamping. */
const PANEL_WIDTH = 320;

/**
 * First-paint estimate only. The real height is measured once the panel mounts and the
 * position is refined, because guessing is how the panel ended up hanging 56px off the
 * bottom of the viewport.
 */
const PANEL_HEIGHT_ESTIMATE = 390;

/** Clock order so the hour list reads like a clock face rather than starting at 1. */
const HOURS_12 = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

const pad = (n: number) => String(n).padStart(2, "0");

const to12Hour = (hour24: number) => {
  const hour12 = hour24 % 12;
  return hour12 === 0 ? 12 : hour12;
};

const to24Hour = (hour12: number, isPm: boolean) => {
  const base = hour12 % 12;
  return isPm ? base + 12 : base;
};

export interface DateTimePickerProps {
  /** Visible field label. Also names the trigger for assistive tech. */
  label: string;
  /** Current value as an ISO instant, or null when nothing is chosen yet. */
  value: string | null;
  /** Called with the new ISO instant. */
  onChange: (iso: string) => void;
  id?: string;
  error?: string;
  disabled?: boolean;
  /** Minute granularity offered. The current value's minute is always included. */
  minuteStep?: number;
  placeholder?: string;
  testId?: string;
  /**
   * Keep the label out of the visual layout but leave it for assistive tech. Use in dense
   * rows whose column header already names the field: the label still has to be distinct,
   * because it is what the panel is announced by, and a row of identically-named pickers is
   * unusable with a screen reader.
   */
  hideLabel?: boolean;
  /**
   * Render the trigger's value in the short form ("Aug 1, 6:00 PM"). Use in table rows, where
   * the long form wraps and — because those rows are fixed-height for the virtualizer — spills
   * over the row below.
   */
  compact?: boolean;
}

/**
 * Date and time field backed by a month grid and an explicit time row.
 *
 * Replaces `<input type="datetime-local">`, whose rendering is browser-controlled
 * and cannot be styled to match the design system. Deliberately mirrors the shape
 * mobile already ships (`mobile/components/ScheduleCallModal.tsx` pairs a calendar
 * with `TimeSelect`) so the two platforms teach the same interaction rather than
 * three different ones.
 *
 * Values are read and written in the viewer's local timezone. See
 * `utils/dateTimeParts.ts` for why that is the correct reading for calls.
 */
export function DateTimePicker({
  label,
  value,
  onChange,
  id,
  error,
  disabled = false,
  minuteStep = DEFAULT_MINUTE_STEP,
  placeholder = "Select date and time...",
  testId,
  hideLabel = false,
  compact = false,
}: DateTimePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // The panel is portalled to the body, so it is positioned from the trigger's viewport rect.
  const { position: panelPosition, reposition: positionPanel } = usePopoverPosition({
    triggerRef,
    panelRef,
    isOpen,
    width: PANEL_WIDTH,
    heightEstimate: PANEL_HEIGHT_ESTIMATE,
  });

  /*
    Falls back to a generated id, so every caller gets a label association rather than only the ones
    that remembered to pass one — the same rule TextInput and HandleInput follow.

    Without it, `htmlFor={undefined}` left the <label> orphaned and the trigger took its accessible
    name from its own text: the formatted value, or "Select date and time...". A list of these — the
    bulk-event review renders two per row — announced as a run of identical buttons with nothing to
    tell one row from the next, which is exactly what this component's `hideLabel` doc warns about.
  */
  const generatedId = useId();
  const triggerId = id ?? generatedId;

  const selected = useMemo(() => isoToLocalParts(value), [value]);

  // The day the grid's roving tabindex currently sits on. Seeded from the value so
  // opening the panel lands keyboard focus on the existing selection.
  const [focusedDay, setFocusedDay] = useState<LocalDateTimeParts>(
    () => selected ?? nowLocalParts()
  );

  // Re-seed each time the panel opens so it never reopens on a stale month.
  useEffect(() => {
    if (isOpen) setFocusedDay(selected ?? nowLocalParts());
  }, [isOpen, selected]);

  // Close on outside pointer-down. mousedown rather than click so the panel closes
  // before a click elsewhere can act on a control it was covering.
  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      // The panel lives in a portal, so it is NOT inside containerRef. Both have to be
      // checked or every click inside the panel would close it.
      if (
        !containerRef.current?.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isOpen]);

  // Move real DOM focus to follow the roving tabindex.
  //
  // Gated on whether the panel is positioned, not on the position object: the grid only
  // exists once the portalled panel has mounted (which is gated on a position being known),
  // but that object is replaced on every repositioning frame, and depending on it dragged
  // focus back into the calendar every time the surrounding modal was scrolled.
  const isPositioned = panelPosition !== null;
  useEffect(() => {
    if (!isOpen || !isPositioned) return;
    const target = gridRef.current?.querySelector<HTMLButtonElement>(
      `[data-day="${focusedDay.year}-${pad(focusedDay.month)}-${pad(focusedDay.day)}"]`
    );
    // The panel is fixed-position and already on screen; letting the browser scroll to the
    // cell would drag whatever the field sits in out from under it.
    target?.focus({ preventScroll: true });
  }, [isOpen, focusedDay, isPositioned]);

  const emit = useCallback(
    (parts: LocalDateTimeParts) => {
      const iso = localPartsToIso(parts);
      if (iso) onChange(iso);
    },
    [onChange]
  );

  const handleSelectDay = useCallback(
    (day: LocalDateTimeParts) => {
      // Picking a day before a time exists gets the default hour rather than
      // midnight, which would read as "no time chosen" to a coach.
      emit({
        ...day,
        hour24: selected?.hour24 ?? DEFAULT_HOUR_24,
        minute: selected?.minute ?? 0,
      });
    },
    [emit, selected]
  );

  const handleTimeChange = useCallback(
    (next: { hour24?: number; minute?: number }) => {
      // Changing the time before a day exists implies today, so the control is
      // usable in either order.
      const base = selected ?? { ...nowLocalParts(), hour24: DEFAULT_HOUR_24, minute: 0 };
      emit({
        ...base,
        hour24: next.hour24 ?? base.hour24,
        minute: next.minute ?? base.minute,
      });
    },
    [emit, selected]
  );

  const handleGridKeyDown = (event: React.KeyboardEvent) => {
    const moves: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
    };

    if (event.key in moves) {
      event.preventDefault();
      setFocusedDay((current) => addDays(current, moves[event.key]));
      return;
    }

    switch (event.key) {
      case "Home":
        event.preventDefault();
        setFocusedDay((current) =>
          addDays(current, -new Date(current.year, current.month - 1, current.day).getDay())
        );
        return;
      case "End":
        event.preventDefault();
        setFocusedDay((current) =>
          addDays(
            current,
            6 - new Date(current.year, current.month - 1, current.day).getDay()
          )
        );
        return;
      case "PageUp":
        event.preventDefault();
        setFocusedDay((current) => addMonths(current, -1));
        return;
      case "PageDown":
        event.preventDefault();
        setFocusedDay((current) => addMonths(current, 1));
        return;
      case "Enter":
      case " ":
        event.preventDefault();
        handleSelectDay(focusedDay);
        return;
      case "Escape":
        event.preventDefault();
        setIsOpen(false);
        triggerRef.current?.focus();
        return;
      default:
    }
  };

  // Six weeks starting from the Sunday on or before the 1st, so the grid height
  // never changes as the user pages through months.
  const gridDays = useMemo(() => {
    const lead = firstWeekdayOfMonth(focusedDay.year, focusedDay.month);
    const first: LocalDateTimeParts = { ...focusedDay, day: 1 };
    return Array.from({ length: 42 }, (_, index) => addDays(first, index - lead));
  }, [focusedDay]);

  const monthLabel = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, {
        month: "long",
        year: "numeric",
      }).format(new Date(focusedDay.year, focusedDay.month - 1, 1)),
    [focusedDay.year, focusedDay.month]
  );

  const today = useMemo(() => nowLocalParts(), []);

  // Offer the stepped minutes, plus the current value's own minute when a
  // previously saved call sits off the step (e.g. an 11:07 legacy value).
  const minuteOptions = useMemo(() => {
    const stepped = new Set<number>();
    for (let minute = 0; minute < 60; minute += Math.max(1, minuteStep)) {
      stepped.add(minute);
    }
    if (selected) stepped.add(selected.minute);
    return [...stepped].sort((a, b) => a - b);
  }, [minuteStep, selected]);

  const isPm = (selected?.hour24 ?? DEFAULT_HOUR_24) >= 12;
  // Memoized alongside its three siblings above, which it was simply missed from. It formats on
  // every render, and a list of these — the bulk-event review renders two per row — makes that a
  // per-keystroke cost across the whole list.
  const triggerLabel = useMemo(
    () =>
      selected
        ? compact
          ? formatLocalPartsShort(selected)
          : formatLocalParts(selected)
        : placeholder,
    [selected, compact, placeholder],
  );

  return (
    <div className="date-time-picker" ref={containerRef}>
      <label
        className={`date-time-picker__label${
          hideLabel ? " date-time-picker__label--hidden" : ""
        }`}
        htmlFor={triggerId}
      >
        {label}
      </label>

      <button
        type="button"
        id={triggerId}
        ref={triggerRef}
        className={`date-time-picker__trigger${
          error ? " date-time-picker__trigger--error" : ""
        }${selected ? "" : " date-time-picker__trigger--placeholder"}`}
        onClick={() => {
          if (!isOpen) positionPanel();
          setIsOpen((open) => !open);
        }}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        data-testid={testId}
      >
        {triggerLabel}
      </button>

      {isOpen &&
        panelPosition &&
        createPortal(
          <div
            className="date-time-picker__panel"
            ref={panelRef}
            role="dialog"
            aria-label={`Choose ${label.toLowerCase()}`}
            style={{ top: panelPosition.top, left: panelPosition.left }}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.stopPropagation();
                setIsOpen(false);
                triggerRef.current?.focus();
              }
            }}
          >
          <div className="date-time-picker__month-nav">
            <button
              type="button"
              className="date-time-picker__month-button"
              onClick={() => setFocusedDay((current) => addMonths(current, -1))}
              aria-label="Previous month"
              data-testid={testId ? `${testId}-prev-month` : undefined}
            >
              &#8249;
            </button>
            <span
              className="date-time-picker__month-label"
              aria-live="polite"
              data-testid={testId ? `${testId}-month` : undefined}
            >
              {monthLabel}
            </span>
            <button
              type="button"
              className="date-time-picker__month-button"
              onClick={() => setFocusedDay((current) => addMonths(current, 1))}
              aria-label="Next month"
              data-testid={testId ? `${testId}-next-month` : undefined}
            >
              &#8250;
            </button>
          </div>

          <div className="date-time-picker__weekdays" aria-hidden="true">
            {WEEKDAY_LABELS.map((weekday) => (
              <span key={weekday} className="date-time-picker__weekday">
                {weekday}
              </span>
            ))}
          </div>

          <div
            className="date-time-picker__grid"
            role="grid"
            ref={gridRef}
            onKeyDown={handleGridKeyDown}
          >
            {gridDays.map((day) => {
              const key = `${day.year}-${pad(day.month)}-${pad(day.day)}`;
              const isCurrentMonth = day.month === focusedDay.month;
              const isSelectedDay = isSameDay(day, selected);
              const isFocusedDay = isSameDay(day, focusedDay);
              const classes = [
                "date-time-picker__day",
                isCurrentMonth ? "" : "date-time-picker__day--outside",
                isSelectedDay ? "date-time-picker__day--selected" : "",
                isSameDay(day, today) ? "date-time-picker__day--today" : "",
              ]
                .filter(Boolean)
                .join(" ");

              return (
                <button
                  key={key}
                  type="button"
                  role="gridcell"
                  data-day={key}
                  data-testid={testId ? `${testId}-day-${key}` : undefined}
                  className={classes}
                  tabIndex={isFocusedDay ? 0 : -1}
                  aria-selected={isSelectedDay}
                  onClick={() => {
                    setFocusedDay(day);
                    handleSelectDay(day);
                  }}
                >
                  {day.day}
                </button>
              );
            })}
          </div>

          <div className="date-time-picker__time-row">

            <select
              className="date-time-picker__time-select"
              aria-label="Hour"
              data-testid={testId ? `${testId}-hour` : undefined}
              value={to12Hour(selected?.hour24 ?? DEFAULT_HOUR_24)}
              onChange={(event) =>
                handleTimeChange({
                  hour24: to24Hour(Number(event.target.value), isPm),
                })
              }
            >
              {HOURS_12.map((hour) => (
                <option key={hour} value={hour}>
                  {hour}
                </option>
              ))}
            </select>

            <span className="date-time-picker__time-separator">:</span>

            <select
              className="date-time-picker__time-select"
              aria-label="Minute"
              data-testid={testId ? `${testId}-minute` : undefined}
              value={selected?.minute ?? 0}
              onChange={(event) =>
                handleTimeChange({ minute: Number(event.target.value) })
              }
            >
              {minuteOptions.map((minute) => (
                <option key={minute} value={minute}>
                  {pad(minute)}
                </option>
              ))}
            </select>

            <div
              className="date-time-picker__meridiem"
              role="group"
              aria-label="AM or PM"
            >
              {[
                { label: "AM", pm: false },
                { label: "PM", pm: true },
              ].map((option) => (
                <button
                  key={option.label}
                  type="button"
                  className={`date-time-picker__meridiem-button${
                    isPm === option.pm
                      ? " date-time-picker__meridiem-button--active"
                      : ""
                  }`}
                  aria-pressed={isPm === option.pm}
                  data-testid={
                    testId ? `${testId}-${option.label.toLowerCase()}` : undefined
                  }
                  onClick={() =>
                    handleTimeChange({
                      hour24: to24Hour(
                        to12Hour(selected?.hour24 ?? DEFAULT_HOUR_24),
                        option.pm
                      ),
                    })
                  }
                >
                  {option.label}
                </button>
              ))}
            </div>

            {/* Explicit commit. Clicking away also saves, but silently — which reads as "that
                did not take" — so the button doubles as confirmation. It sits in the time row
                rather than its own footer so the panel does not grow tall enough to flip above
                the field again. */}
            <button
              type="button"
              className="date-time-picker__done-button"
              onClick={() => {
                setIsOpen(false);
                triggerRef.current?.focus();
              }}
              data-testid={testId ? `${testId}-done` : undefined}
            >
              Done
            </button>
          </div>
          </div>,
          document.body
        )}

      {error && (
        <span className="date-time-picker__error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
